import React, { useState } from 'react';

const GameSplashScreen = ({ onStartGame, currentUser }) => {
  const [selectedMode, setSelectedMode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const gameModes = [
    {
      id: 'career',
      title: 'Career Mode',
      description: 'Continue your jumping adventure from where you left off',
      icon: '🏆',
      color: 'from-blue-500 to-purple-600',
      available: true
    },
    {
      id: 'teacher_mode',
      title: 'Teacher Mode',
      description: 'Enter a teacher code to start a quiz challenge',
      icon: '📚',
      color: 'from-orange-500 to-red-500',
      available: true
    },
    {
      id: 'challenge_friends',
      title: 'Challenge Your Friends',
      description: 'Enter a friend\'s quiz code to play their challenge',
      icon: '⚡',
      color: 'from-purple-500 to-pink-500',
      available: true
    }
  ];

  const handleModeSelect = async (mode) => {
    if (!mode.available) return;

    setSelectedMode(mode.id);
    setIsLoading(true);

    // Add a small delay to show loading state
    setTimeout(() => {
      onStartGame(mode.id);
    }, 500);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-white mb-2">Loading Game...</h2>
          <p className="text-gray-300">Preparing your adventure</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex flex-col">
      {/* Back Button */}
      <div className="absolute top-4 left-4 z-10">
        <button
          onClick={() => window.history.back()}
          className="bg-black bg-opacity-30 hover:bg-opacity-50 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 backdrop-blur-sm"
        >
          ← Back
        </button>
      </div>

      {/* Header */}
      <div className="text-center pt-8 pb-4">
        <h1 className="text-5xl font-bold text-white mb-2 drop-shadow-lg">
          🐸 Brain Climb
        </h1>
        <p className="text-xl text-gray-300">How are we playing/learning today?</p>
      </div>

      {/* Game Mode Selection */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl w-full">
          {gameModes.map((mode) => (
            <div
              key={mode.id}
              onClick={() => handleModeSelect(mode)}
              className={`
                relative p-6 rounded-2xl shadow-xl transform transition-all duration-200 cursor-pointer
                ${mode.available
                  ? 'hover:scale-105 hover:shadow-2xl'
                  : 'opacity-60 cursor-not-allowed'
                }
                bg-gradient-to-br ${mode.color}
              `}
            >
              {/* Coming Soon Badge */}
              {mode.comingSoon && (
                <div className="absolute top-2 right-2 bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded-full">
                  Coming Soon
                </div>
              )}

              <div className="text-center">
                <div className="text-6xl mb-4">{mode.icon}</div>
                <h3 className="text-2xl font-bold text-white mb-2">{mode.title}</h3>
                <p className="text-gray-200 mb-4">{mode.description}</p>

                {mode.available && (
                  <div className="text-sm text-white bg-white bg-opacity-20 rounded-full px-4 py-2 inline-block">
                    Click to start!
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* How to Play Section */}
      <div className="p-8 bg-black bg-opacity-10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-8">How to Play</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl mb-3">🐸</div>
              <h3 className="text-lg font-semibold text-white mb-2">Goal</h3>
              <p className="text-gray-300 text-sm">Help the frog reach new heights by jumping from platform to platform!</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="text-lg font-semibold text-white mb-2">Energy</h3>
              <p className="text-gray-300 text-sm">Each jump costs energy. Answer 3 questions to refill your energy!</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-3">🎮</div>
              <h3 className="text-lg font-semibold text-white mb-2">Controls</h3>
              <p className="text-gray-300 text-sm">Use arrow keys or A/D to move, Space/W to jump. Triple jump available!</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-3">💎</div>
              <h3 className="text-lg font-semibold text-white mb-2">Collect</h3>
              <p className="text-gray-300 text-sm">Grab gems and coins as you climb for bonus rewards and points!</p>
            </div>
          </div>
        </div>
      </div>

      {/* User Welcome */}
      {currentUser && (
        <div className="p-6 bg-black bg-opacity-20">
          <div className="max-w-6xl mx-auto text-center">
            <div className="text-gray-300">
              Welcome back, {currentUser.name}!
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameSplashScreen;
